// src/entities/form/ui-tailwind/TextInput.tsx
'use client';

import type { FC, InputHTMLAttributes } from 'react';
import { useId, useState } from 'react';
import cn from 'classnames';

import FieldWrapper from './FieldWrapper';
import type { BaseFieldProps } from '@/entities/form/model-tailwind/form.model';
import { withFormState } from '@/entities/form/model-tailwind/with-form-state';

import { Eye, EyeOff } from 'lucide-react';
import { Button, Input } from '@/shared/ui-kit/src';

type HTMLInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>;

type ExtraProps = {
  /** default true to keep current behavior */
  disableWhenReadOnly?: boolean;
  /** extra classes applied to the input element */
  inputClassName?: string;
  /** if true, renders a show/hide password toggle */
  isPassword?: boolean;
  extraClassName?: string;
};

const TextInputComponent: FC<BaseFieldProps<HTMLInputProps> & ExtraProps> = ({
  label,
  error,
  disabled,
  readOnly,
  size = 'middle',
  placeholder,
  wrapperClassName,
  horizontal,
  extra,
  extraClassName,
  disableWhenReadOnly = true,
  inputClassName,
  type = 'text',
  id,
  isPassword = false,
  autoComplete,
  ...props
}) => {
  const reactId = useId();
  const inputId = id ?? `tw-input-${reactId}`;
  const isDisabled = disabled || (disableWhenReadOnly && readOnly);

  const [show, setShow] = useState(false);
  const effectiveType = isPassword ? (show ? 'text' : 'password') : type;
  const effectiveAutocomplete = autoComplete ?? (isPassword ? 'current-password' : 'username');

  const iconClasses = 'h-4 w-4 opacity-70 group-hover:opacity-100 transition';

  return (
    <FieldWrapper label={label} error={error} disabled={isDisabled} readOnly={readOnly} wrapperClassName={wrapperClassName} horizontal={horizontal}>
      <div className={cn('relative', isPassword && 'group')}>
        <Input
          id={inputId}
          type={effectiveType}
          placeholder={placeholder}
          readOnly={readOnly}
          disabled={isDisabled}
          autoComplete={effectiveAutocomplete}
          aria-describedby={error ? `${inputId}-error` : undefined}
          size={size}
          error={!!error}
          className={cn(
            // give room for the eye button
            isPassword && 'pr-10',
            inputClassName
          )}
          {...props}
        />

        {isPassword && !readOnly && (
          <Button
            variant='plain'
            type='button'
            onClick={() => setShow((v) => !v)}
            disabled={isDisabled}
            aria-label={show ? 'Hide password' : 'Show password'}
            title={show ? 'Hide password' : 'Show password'}
            className={cn(
              'tw-password-toggle',
              'absolute right-2 top-1/2 -translate-y-1/2',
              'inline-flex items-center justify-center',
              'rounded-md p-1',
              'bg-[rgb(38,38,38)] text-white',
              'focus:outline-none focus:ring-0 focus-visible:outline-none',
              'disabled:opacity-60'
            )}
          >
            {show ? <EyeOff className={iconClasses} /> : <Eye className={iconClasses} />}
          </Button>
        )}
      </div>

      {error && (
        <span id={`${inputId}-error`} hidden>
          {String(error)}
        </span>
      )}

      <div className={cn('mt-1 min-h-6', extraClassName)}>{extra ?? null}</div>
    </FieldWrapper>
  );
};

TextInputComponent.displayName = 'TextInputComponent';

export const TextInput = withFormState(TextInputComponent);
export default TextInput;
export const RawTextInput = TextInputComponent;
